import { useEffect, useState } from "react";
import { NavigateOptions, useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { BellIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { xSymbolUnicode } from "../../app/utils";
import Avatar from "../../components/Avatar";
import { Popover, PopoverContent, PopoverHeading, PopoverTrigger } from "../../components/Popover";
import { useLazyGetDraftPromptIdQuery } from "../writing/drafting/draftingApiSlice";
import { handleSelectNotification } from "./notificationUtil";
import {
	NotificationBaseViewModel,
	useLastNotificationsQuery,
	useLazyDraftActionQuery,
	useSetNotificationsAsViewedMutation,
	useUnreadNotificationCountQuery,
} from "./notificationsApiSlice";

export default function NotificationPopover() {
	const { role, requiresAccountConfirmation } = useAppSelector((state) => state.user);
	const [open, setOpen] = useState(false);
	const [notificationId, setNotificationId] = useState<number>();
	const [notificationUrl, setNotificationUrl] = useState<string>();
	const [notificationUrlOptions, setNotificationUrlOptions] = useState<NavigateOptions>();

	const navigate = useNavigate();

	const [callGetDraftAction] = useLazyDraftActionQuery();
	const [callGetDraftPromptId] = useLazyGetDraftPromptIdQuery();
	const [callSetAsViewed] = useSetNotificationsAsViewedMutation();

	const unreadResult = useUnreadNotificationCountQuery();
	const unreadCount = unreadResult?.data?.count ?? 0;

	const lastNotificationsResult = useLastNotificationsQuery();
	const lastNotifications = lastNotificationsResult.data?.notificationModels;

	useEffect(() => {
		if (notificationUrl) {
			if (notificationId) handleMarkAsRead(notificationId);
			navigate(notificationUrl, notificationUrlOptions);
			setOpen(false);
			setNotificationUrl(undefined);
		}
	}, [notificationUrl]);

	function handleMarkAsRead(rowId: number) {
		const singleId: number[] = [rowId];
		callSetAsViewed(singleId);
	}

	function handleViewAllClick() {
		requiresAccountConfirmation ? navigate("/myAccount") : navigate(`/notifications`);
		setOpen(false);
	}

	function handleNotificationClick(event: any, notification: NotificationBaseViewModel) {
		if (role) {
			handleSelectNotification(notification, role, {
				setNotificationId,
				setNotificationUrl,
				setNotificationUrlOptions,
				callGetDraftAction: (draftId: number) => callGetDraftAction(draftId).unwrap(),
				callGetDraftPromptId: (draftId: number) => callGetDraftPromptId(draftId).unwrap(),
			});
		}
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger id="notification-button" onClick={() => setOpen((v) => !v)}>
				<div className="flex relative cursor-pointer">
					<div
						data-tooltip-id="notifications-icon-tooltip"
						className="h-10 w-10 flex overflow-hidden justify-center items-center rounded-full bg-white/10 ring-2 ring-white/10 hover:bg-white/30 hover:ring-white/60 hover:opacity-85 focus:outline-hidden focus:ring-2 focus:ring-white">
						<BellIcon
							className="h-8 w-8 stroke-white"
							aria-label="View notifications."
						/>
					</div>
					{unreadCount > 0 && (
						<span
							id="notification-badge"
							className="absolute rounded-full leading-5 bg-red-100 w-5 h-5 text-xs font-medium text-red-700 left-5 bottom-5">
							{unreadCount > 99 ? "99+" : unreadCount}
						</span>
					)}
					<Tooltip
						id="notifications-icon-tooltip"
						place="bottom-end"
						content="Notifications"
						className="z-40"></Tooltip>
				</div>
			</PopoverTrigger>
			<PopoverContent
				id="notification-content"
				className="popover bg-white rounded-md border border-gray-200 shadow-md shadow-gray-200 overflow-hidden z-[2]">
				<PopoverHeading className="flex justify-between py-2 px-4 bg-gray-100 border-b border-b-gray-200 ">
					<div className="text-base font-bold text-primary-700">Notifications</div>
					<div className="cursor-pointer" onClick={() => setOpen(false)}>
						{xSymbolUnicode}
					</div>
				</PopoverHeading>
				<div className="max-h-[420px] overflow-auto">
					{lastNotifications?.length == 0 ? (
						<div className="w-96">
							<span className="block py-2.5 px-4">No notifications</span>
							<hr></hr>
						</div>
					) : (
						lastNotifications?.map((notification) => (
							<IndividualNotification
								key={notification.id}
								notification={notification}
								className={notification.viewed ? "" : "bg-green-50"}
								onClick={(e) => handleNotificationClick(e, notification)}
							/>
						))
					)}
				</div>
				<div className="block py-2.5 text-center text-sm font-semibold leading-6">
					<a className="cursor-pointer" onClick={() => handleViewAllClick()}>
						View all notifications
					</a>
				</div>
			</PopoverContent>
		</Popover>
	);
}

interface IndividualNotificationProps {
	notification: NotificationBaseViewModel;
	className?: string;
	onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

function IndividualNotification(props: IndividualNotificationProps) {
	return (
		<section
			className="w-96"
			id={props.notification.viewed ? "" : "unread-individual-notification"}>
			<div
				className={`${props.className} flex relative min-h-[80px] py-2 px-4 hover:cursor-pointer`}
				onClick={props.onClick}>
				<div className="content-center">
					<Avatar
						hasImage={props.notification.avatarUserHasImage}
						initials={props.notification.avatarUserInitials ?? "?"}
						userId={props.notification.avatarUserId}
						className="h-12 w-12"
					/>
				</div>
				<div
					className="text-xs px-3"
					dangerouslySetInnerHTML={{
						__html: props.notification.body,
					}}
				/>
			</div>
			<hr></hr>
		</section>
	);
}
